import { opsSession } from '../../lib/sessions';

export const safeGetItem = (key: string): string | null => {
  try {
    if (typeof localStorage !== 'undefined' && localStorage) {
      return localStorage.getItem(key);
    }
  } catch (e) {
    console.warn('localStorage.getItem error:', e);
  }
  return null;
};

export const safeSetItem = (key: string, value: string): void => {
  try {
    if (typeof localStorage !== 'undefined' && localStorage) {
      localStorage.setItem(key, value);
    }
  } catch (e) {
    console.warn('localStorage.setItem error:', e);
  }
};

export const safeRemoveItem = (key: string): void => {
  try {
    if (typeof localStorage !== 'undefined' && localStorage) {
      localStorage.removeItem(key);
    }
  } catch (e) {
    console.warn('localStorage.removeItem error:', e);
  }
};

export const safeKeys = (): string[] => {
  try {
    if (typeof localStorage !== 'undefined' && localStorage) {
      return Object.keys(localStorage);
    }
  } catch (e) {
    console.warn('localStorage keys error:', e);
  }
  return [];
};

export const getSkew = (): number => {
  const raw = safeGetItem('ops_server_time_skew');
  if (raw) {
    const parsed = parseInt(raw, 10);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

export interface OpsSyncMessage {
  type: 'REFETCH_TIMER';
  /** Opaque Firebase uid of the sender's account; never a token. */
  ownerUid?: string | null;
}

/**
 * Timer broadcasts are shared by every tab on the origin, including tabs that
 * belong to another staff account. A message stamped with a different owner
 * must not trigger work in this session. Messages without an owner (older
 * tabs) only ask for a refetch of the receiver's own data and stay accepted.
 */
export const isOpsSyncForCurrentSession = (
  data: unknown,
  currentUid: string | null = opsSession.current.uid,
): boolean => {
  if (!data || typeof data !== 'object') return false;
  const message = data as Partial<OpsSyncMessage>;
  if (message.type !== 'REFETCH_TIMER') return false;
  if (typeof message.ownerUid !== 'string' || currentUid === null) return true;
  return message.ownerUid === currentUid;
};

export const broadcastOpsSync = (): void => {
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel('mbbe-timer-sync');
      const message: OpsSyncMessage = { type: 'REFETCH_TIMER', ownerUid: opsSession.current.uid };
      channel.postMessage(message);
      channel.close();
    }
  } catch (e) {
    console.warn('BroadcastSync error:', e);
  }
};

export interface TabActivity {
  tabId: string;
  updatedAt: number;
  visible: boolean;
  focused: boolean;
}

export const isValidTabActivity = (data: unknown): data is TabActivity => {
  if (!data || typeof data !== 'object') return false;
  const act = data as Record<string, unknown>;

  if (typeof act.tabId !== 'string') return false;
  if (typeof act.visible !== 'boolean') return false;
  if (typeof act.focused !== 'boolean') return false;
  if (typeof act.updatedAt !== 'number' || isNaN(act.updatedAt)) return false;

  // Prevent future timestamp exploits. Allow up to 10 seconds of future skew just in case.
  if (act.updatedAt > Date.now() + 10_000) return false;

  return true;
};

/**
 * Finish-note drafts are recoverable work and are kept across sign-out. Each
 * draft is keyed by its work-session id and stamped with the owning account
 * and last-write time (a separate metadata key keeps the draft text itself in
 * its original key). Another account never reads, overwrites or deletes it;
 * unchanged drafts expire after DRAFT_TTL_MS.
 */
export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const DRAFT_PREFIX = 'ops_finish_note_draft_';
const DRAFT_OWNER_PREFIX = 'ops_finish_note_owner_';

interface DraftOwner {
  ownerUid: string;
  savedAt: number;
}

const readDraftOwner = (timerId: string): DraftOwner | null => {
  const raw = safeGetItem(`${DRAFT_OWNER_PREFIX}${timerId}`);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<DraftOwner>;
    if (typeof parsed.ownerUid === 'string' && typeof parsed.savedAt === 'number') {
      return { ownerUid: parsed.ownerUid, savedAt: parsed.savedAt };
    }
  } catch {
    // fall through: unreadable metadata is treated as missing
  }
  return null;
};

const dropDraft = (timerId: string): void => {
  safeRemoveItem(`${DRAFT_PREFIX}${timerId}`);
  safeRemoveItem(`${DRAFT_OWNER_PREFIX}${timerId}`);
};

const isExpired = (owner: DraftOwner | null, now: number): boolean =>
  owner !== null && now - owner.savedAt > DRAFT_TTL_MS;

const belongsToAnotherAccount = (owner: DraftOwner | null, uid: string | null): boolean =>
  owner !== null && uid !== null && owner.ownerUid !== uid;

export const readFinishNoteDraft = (
  timerId: string | null | undefined,
  uid: string | null = opsSession.current.uid,
  now: number = Date.now(),
): string => {
  if (!timerId) return '';
  const owner = readDraftOwner(timerId);
  if (isExpired(owner, now)) {
    dropDraft(timerId);
    return '';
  }
  if (belongsToAnotherAccount(owner, uid)) return '';
  return safeGetItem(`${DRAFT_PREFIX}${timerId}`) ?? '';
};

export const writeFinishNoteDraft = (
  timerId: string | null | undefined,
  value: string,
  uid: string | null = opsSession.current.uid,
  now: number = Date.now(),
): void => {
  if (!timerId) return;
  if (belongsToAnotherAccount(readDraftOwner(timerId), uid)) return;
  safeSetItem(`${DRAFT_PREFIX}${timerId}`, value);
  if (uid !== null) safeSetItem(`${DRAFT_OWNER_PREFIX}${timerId}`, JSON.stringify({ ownerUid: uid, savedAt: now }));
};

/** `uid` is the account that owns the work, captured when the action started. */
export const removeFinishNoteDraft = (
  timerId: string | null | undefined,
  uid: string | null = opsSession.current.uid,
): void => {
  if (!timerId) return;
  if (belongsToAnotherAccount(readDraftOwner(timerId), uid)) return;
  dropDraft(timerId);
};

export const pruneExpiredFinishNoteDrafts = (now: number = Date.now()): void => {
  for (const key of safeKeys()) {
    if (!key.startsWith(DRAFT_OWNER_PREFIX)) continue;
    const timerId = key.slice(DRAFT_OWNER_PREFIX.length);
    const owner = readDraftOwner(timerId);
    if (owner === null) safeRemoveItem(key); // unreadable metadata only; the draft text is kept
    else if (isExpired(owner, now)) dropDraft(timerId);
  }
};
